import { SxProps, Theme } from "@mui/material";
import reportsTableTokens from "./ReportsTable.tokens";
import { COMPACT_QUERY } from "./Reports.tokens";

const t = reportsTableTokens;

/**
 * `CustomTable` overrides that turn the shared table into the one
 * `Reports.dc.html` draws. Handed to the component through `cardSx` /
 * `tableSxOverride`, so no other table in the app is touched.
 *
 * The rules here repeat the base rule set's descendant selectors on purpose:
 * equal specificity plus later source order is what lets them win. Everything
 * is desktop-only — `CustomTable` is not rendered below `md` on this screen
 * (`ReportsRecordList` draws cards there), but the guard keeps the override
 * from leaking if that ever changes.
 */

/** Outer card: 16px corners and the design's hairline + shadow. */
export const reportsTableCardSx: SxProps<Theme> = {
  bgcolor: t.card.surface,
  border: `${t.card.borderWidth} solid ${t.card.border}`,
  borderRadius: t.card.radius,
  boxShadow: t.card.shadow,
};

/** Header strip, rows, cells and the selection box. */
export const reportsTableSx: SxProps<Theme> = {
  // ---- Column headers ---------------------------------------------------
  "& .MuiTableHead-root .MuiTableCell-root": {
    px: 0,
    py: t.header.paddingY,
    bgcolor: t.header.bg,
    color: t.header.color,
    fontSize: t.header.fontSize,
    fontWeight: t.header.fontWeight,
    letterSpacing: t.header.letterSpacing,
    textTransform: t.header.textTransform,
    borderTop: "none",
    borderBottom: `${t.card.borderWidth} solid ${t.header.borderBottom}`,
    "&:first-of-type": { pl: t.gutter, pr: 0, width: t.checkbox.columnWidth },
    "&:nth-of-type(2)": { pl: t.columnGap },
    "&:last-of-type": { pr: t.gutter },
  },

  // ---- Body cells -------------------------------------------------------
  "& .MuiTableBody-root .MuiTableCell-root": {
    px: 0,
    py: t.row.paddingY,
    height: "auto",
    color: t.row.valueColor,
    fontSize: t.row.valueFontSize,
    fontWeight: t.row.valueFontWeight,
    borderBottom: `${t.card.borderWidth} solid ${t.row.divider}`,
    "&:first-of-type": { pl: t.gutter, pr: 0, width: t.checkbox.columnWidth },
    "&:nth-of-type(2)": { pl: t.columnGap },
    "&:last-of-type": { pr: t.gutter },
  },

  // The last row closes the card, so it drops its rule.
  "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
    borderBottom: "none",
  },

  "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root": {
    backgroundColor: t.row.hoverBg,
  },
  "& .MuiTableBody-root .MuiTableRow-root.Mui-selected": {
    backgroundColor: `${t.row.selectedBg} !important`,
    "&:hover": { backgroundColor: `${t.row.selectedBg} !important` },
  },

  // No `.MuiCheckbox-root` rule: selection uses the app's own control — the
  // 18px SVG pair on `MuiCheckbox.defaultProps` plus `CustomTable`'s
  // `palette.selection` colours — exactly as every other table renders it.
  // `Reports.dc.html` draws a bespoke rounded square instead; it is not
  // followed, for the same reason as the chrome in `ReportsTable.tokens.ts`.

  // ---- Trailing (right-aligned) columns ---------------------------------
  // `numStyle` in the mock is `text-align:right` on every column after the
  // identity one, so the values line up on their right edge rather than
  // floating in the middle of the track. The `align` prop on each header does
  // the alignment; the weight/colour come from the body-cell rule above.

  [COMPACT_QUERY]: {},
};

/**
 * The identity cell — name over sub-line.
 *
 * These are the mock's literal declarations rather than MUI `Typography`
 * variants: `body1`/`body2` resolve to the theme's 16/14px Montserrat at the
 * theme's weights and colours, which is what made this column read a size too
 * large and a shade too dark against the reference.
 */
export const reportsTableIdentitySx: SxProps<Theme> = {
  minWidth: 0,
};

const clamp = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

export const reportsTableNameSx: SxProps<Theme> = {
  fontSize: t.row.nameFontSize,
  fontWeight: t.row.nameFontWeight,
  color: t.row.nameColor,
  lineHeight: "normal",
  ...clamp,
};

export const reportsTableSubSx: SxProps<Theme> = {
  fontSize: t.row.subFontSize,
  fontWeight: 400,
  color: t.row.subColor,
  lineHeight: "normal",
  mt: t.row.subMarginTop,
  ...clamp,
};

/**
 * The one column that reads a step stronger than the rest — Recipient
 * analytics' "Completion Rate" (`rateStyle` in the mock). Applied to the cell's
 * own content, so it beats the body-cell rule that sets 600/#4A4462.
 */
export const reportsTableStrongValueSx: SxProps<Theme> = {
  fontSize: t.row.strongFontSize,
  fontWeight: t.row.strongFontWeight,
  color: t.row.strongColor,
};

/**
 * Height of a loading row.
 *
 * `CustomTable`'s loader draws one bar per header, so it cannot know that the
 * leading cell here stacks a name over an email — without this the loading
 * table came out 43px a row against a real 61px and shrank as the report
 * arrived. Derived from the same tokens as the cell itself; the 1.25 stands in
 * for `line-height: normal`, which has no numeric token.
 */
export const reportsLoadingRowHeight =
  `calc(${t.row.paddingY} * 2 + ${t.row.nameFontSize} * 1.25 + ` +
  `${t.row.subMarginTop} + ${t.row.subFontSize} * 1.25)`;

export default reportsTableSx;
